import { memo } from 'react'
import { Attention, Close } from '@icon-park/react'

import { Tokens } from '@/assets/tokens'
import { Button, Flex, Form, Input, Selector, Uploader } from '@/components'
import { useFileUploader, useInput, useRouting } from '@/hooks'
import { Alert, Api } from '@/modules'
import { ChannelImportResult, CreateChannelRequest, SearchResponse } from '@shared/models'
import { Nbr } from '@/utils/numbers'
import { Str } from '@/utils/strings'
import { useCompanyContext } from '../../../CompanyDetails.context'
import { LogoContainer } from '../../../CompanyDetails.styles'
import { UploaderViewer } from '@/components/derived'
import { handleWeightChange } from '../../../utils'
import { ExcelUpload } from '../uploader/ExcelUploader'
import { Divider } from '../uploader/ExcelUploader.styles'
import { ExportReportButton } from '../export-report/ExportReportButton'
import { ImportResultContent } from '../stats/Stats'
import { DialogContainer, DialogOverlay, ModalBody, ModalHeaderContainer } from './AddChannelFormModal.style'
import { useContext, useEffect, useState } from 'react'
import { DialogContext } from '@/modules/dialog/Dialog.context'

const MIN_WEIGHT = 1
const MAX_WEIGHT = 5

type Props = {
  onSuccess: () => void
}

export const AddChannelFormModal = memo(Component)

function Component({ onSuccess }: Props): JSX.Element | null {
  const { companyID, changeQueryParam, currentPage } = useRouting()
  const { company, loadChannels } = useCompanyContext()
  const { sendAlert } = Alert.Hook()
  const dialog = useContext(DialogContext)

  const [excelFile, setExcelFile] = useState<File | null>(null)
  const [ignorarVerificacao, setIgnorarVerificacao] = useState(false)
  const [ignorarCanal, setIgnorarCanal] = useState(false)

  const [isSearchingVehicles, searchVehicle, searchedVehicles = []] = Api.Query<SearchResponse[]>(`vehicles/search`)
  const { isRunning: isCreatingChannel, run: createChannel } = Api.Command<void>(Api.Methods.POST, `channels`)
  const { isRunning: isImportingChannels, run: importChannels } = Api.Command<ChannelImportResult>(
    Api.Methods.POST,
    `channels/${companyID}/import`,
  )

  const { value: name, binder: nameBinder, reset: resetName } = useInput()
  const { value: mediaLink, binder: mediaLinkBinder, reset: resetMediaLink } = useInput()
  const { value: weight, binder: weightBinder, reset: resetWeight } = useInput('2')
  const { selectedOption: selectedVehicle, binder: vehicleBinder, reset: resetVehicle } = Selector.Binder()
  const { selectedOption: selectedTier, binder: tierBinder, reset: resetTier } = Selector.Binder()
  const { selectedOption: selectedSource, binder: sourceBinder, reset: resetSource } = Selector.Binder()
  const { value: price, binder: priceBinder, reset: resetPrice } = useInput()
  const { value: country, binder: countryBinder, reset: resetCountry } = useInput()
  const { value: region, binder: regionBinder, reset: resetRegion } = useInput()
  const { value: state, binder: stateBinder, reset: resetState } = useInput()
  const { value: roundedLogo, bind: roundedLogoBinder, reset: resetRoundedLogo } = useFileUploader()
  const { value: thumbnail, bind: thumbnailBinder, reset: resetThumbnail } = useFileUploader()

  const tiers = ['SUPER TIER', 'TIER 1', 'TIER 2', 'TIER 3', 'TIER 4', 'TIER 5']
  const dataSources = [
    { text: 'Redes', value: 1 },
    { text: 'Imprensa', value: 2 },
  ]

  useEffect(() => {
    searchVehicle({})
  }, [searchVehicle])

  function handleClose(): void {
    changeQueryParam({ param: 'addChannel', value: undefined })
  }

  function resetForm(): void {
    resetName()
    resetMediaLink()
    resetWeight()
    resetVehicle()
    resetTier()
    resetSource()
    resetPrice()
    resetCountry()
    resetRegion()
    resetState()
    setIgnorarVerificacao(false)
    setIgnorarCanal(false)
    resetRoundedLogo()
    resetThumbnail()
  }

  function handleExcelImport(): void {
    if (!excelFile) return

    const formData = new FormData()
    formData.append('file', excelFile)

    importChannels<FormData>(
      {
        input: formData,
        onSuccess: (result: ChannelImportResult) => {
          const fileForReport = excelFile
          const { rows, ...displayResult } = result
          setExcelFile(null)
          loadChannels({ page: currentPage })
          dialog?.setData({
            title: 'Resultado da Importação',
            content: <ImportResultContent {...displayResult} />,
            closeText: 'Fechar',
            customActions: (
              <>
                <Button.Root $variant="outlined" onClick={() => dialog?.close()}>
                  <Button.Text value="Fechar" />
                </Button.Root>
                {fileForReport && companyID && (
                  <ExportReportButton
                    file={fileForReport}
                    companyID={companyID}
                    companyName={company?.name}
                    rows={rows}
                  />
                )}
              </>
            ),
            justifiedButtons: true,
          })
          dialog?.open()
        },
      },
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
  }

  function handleSubmit(): void {
    if (!companyID) return

    const nameStr = Str.from(name)
    if (nameStr.isBlank) {
      sendAlert({ type: 'error', title: 'Campo obrigatório', message: "Campo 'Nome do canal' é obrigatório" })
      return
    }

    const weightNbr = Nbr.from(weight)
    if (weightNbr.isBlank) {
      sendAlert({ type: 'error', title: 'Campo obrigatório', message: "Campo 'Peso' é obrigatório" })
      return
    }

    const vehicleStr = Str.from(selectedVehicle?.value)
    if (vehicleStr.isBlank) {
      sendAlert({ type: 'error', title: 'Campo obrigatório', message: "Campo 'Veículo' é obrigatório" })
      return
    }

    const tierStr = Str.from(selectedTier?.value)
    if (tierStr.isBlank) {
      sendAlert({ type: 'error', title: 'Campo obrigatório', message: "Campo 'Tier' é obrigatório" })
      return
    }

    const sourceNbr = Nbr.from(selectedSource?.value)
    if (sourceNbr.isBlank) {
      sendAlert({ type: 'error', title: 'Campo obrigatório', message: "Campo 'Fonte de Dados' é obrigatório" })
      return
    }

    const input = {
      companyID,
      name: nameStr.val,
      mediaLink: Str.from(mediaLink).val,
      weight: weightNbr.val,
      vehicle: vehicleStr.val,
      tier: tierStr.val,
      source: sourceNbr.val,
      price: Nbr.from(price).value,
      country: Str.from(country).value,
      region: Str.from(region).value,
      state: Str.from(state).value,
      ignorarVerificacao,
      ignorarCanal,
      roundedLogo,
      thumbnail,
    } as CreateChannelRequest

    createChannel<CreateChannelRequest>({
      input,
      onSuccess: () => {
        sendAlert({ type: 'success', title: 'Sucesso', message: 'Canal cadastrado com sucesso!' })
        resetForm()
        loadChannels({ page: currentPage })
        onSuccess()
      },
    })
  }

  return (
    <DialogOverlay>
      <DialogContainer>
        <ModalHeaderContainer $type="row" $justify="space-between" $align="stretch" $gap={20} $fit>
          <h1>Adicionar Canal</h1>
          <Button.Root $variant="link" type="button" onClick={handleClose}>
            <Button.Icon value={Close} color={Tokens.ColorTextPrimary} />
          </Button.Root>
        </ModalHeaderContainer>
        <ModalBody>
          <Flex $type="column" $align="stretch" $gap={20} $fit>
            <ExcelUpload
              file={excelFile}
              onFileSelect={setExcelFile}
              onImport={handleExcelImport}
              isImporting={isImportingChannels}
              acceptedFormats={['.xls', '.xlsx']}
            />

            <Divider>ou cadastre manualmente</Divider>

            <Form onSubmit={handleSubmit} controlParam={isCreatingChannel}>
              <Input label="Nome do canal" placeholder="Nome" {...nameBinder} required />
              <Flex $type="row" $align="flex-start" $gap={24}>
                <Input label="Endereço web do canal" placeholder="Link do Canal" {...mediaLinkBinder} />
                <Input
                  label="Peso"
                  placeholder="Peso"
                  type="number"
                  maxLength={3}
                  {...weightBinder}
                  required
                  onChange={(value: string) => handleWeightChange(weightBinder, MIN_WEIGHT, MAX_WEIGHT, value)}
                />
              </Flex>
              <Flex $type="row" $align="flex-start" $gap={24}>
                <Selector.Searchable
                  {...vehicleBinder}
                  label="Tipo de mídia"
                  placeholder="Tipo de mídia"
                  options={searchedVehicles.map((vehicle) => ({ ...vehicle }))}
                  onSearch={(searchParam: string): void => {
                    searchVehicle({ name: searchParam })
                  }}
                  loading={isSearchingVehicles}
                  required
                />
                <Selector.Searchable
                  {...tierBinder}
                  label="Tier"
                  placeholder="Tier"
                  options={tiers.map((tier) => ({ text: tier, value: tier }))}
                  onSearch={(): void => {}}
                  loading={isSearchingVehicles}
                  required
                />
              </Flex>
              <Selector.Searchable
                {...sourceBinder}
                label="Fonte de Dados"
                placeholder="Fonte de Dados"
                options={dataSources}
                onSearch={(): void => {}}
                required
              />
              <Flex $type="row" $justify="center" $align="center" $gap={24} $fit>
                <LogoContainer>
                  <Uploader
                    label="Logo circular (.svg 40px/40px)"
                    icon={Attention}
                    iconColor="red"
                    {...roundedLogoBinder}
                    requiredExtensions={['svg']}
                    required
                  />
                  <UploaderViewer value={roundedLogo} />
                </LogoContainer>
                <LogoContainer>
                  <Uploader
                    label="Logo por extenso (.svg 203px/50px)"
                    icon={Attention}
                    iconColor="red"
                    {...thumbnailBinder}
                    requiredExtensions={['svg']}
                    required
                  />
                  <UploaderViewer value={thumbnail} />
                </LogoContainer>
              </Flex>
              <Flex $type="row" $justify="space-between" $align="flex-start" $gap={24}>
                <Input
                  preMask="R$"
                  label="Preço base"
                  placeholder="Preço"
                  type="number"
                  maxLength={10}
                  {...priceBinder}
                />
                <Input label="País" placeholder="País" {...countryBinder} />
              </Flex>
              <Flex $type="row" $justify="space-between" $align="flex-start" $gap={24}>
                <Input label="Região" placeholder="Região" {...regionBinder} />
                <Input label="Estado" placeholder="Estado" maxLength={2} {...stateBinder} />
              </Flex>
              <Flex $type="row" $align="center" $gap={8}>
                <input
                  type="checkbox"
                  id="ignorar-verificacao-add"
                  checked={ignorarVerificacao}
                  onChange={(e) => setIgnorarVerificacao(e.target.checked)}
                />
                <label htmlFor="ignorar-verificacao-add" style={{ fontSize: 14, cursor: 'pointer' }}>
                  Ignorar verificação de termos (postagens deste canal entram sem verificar conteúdo)
                </label>
              </Flex>
              <Flex $type="row" $align="center" $gap={8}>
                <input
                  type="checkbox"
                  id="ignorar-canal-add"
                  checked={ignorarCanal}
                  onChange={(e) => setIgnorarCanal(e.target.checked)}
                />
                <label htmlFor="ignorar-canal-add" style={{ fontSize: 14, cursor: 'pointer' }}>
                  Ignorar canal (postagens deste canal não entram no sistema)
                </label>
              </Flex>
              <Flex $type="row" $justify="flex-end" $align="stretch" $gap={20} $fit>
                <Button.Root type="submit" $variant="primary" $loading={isCreatingChannel}>
                  <Button.Text value="Adicionar" />
                </Button.Root>
              </Flex>
            </Form>
          </Flex>
        </ModalBody>
      </DialogContainer>
    </DialogOverlay>
  )
}
